import React, { useState } from 'react'
import styled from 'styled-components'
import { string } from 'prop-types'
import { connect, getIn } from 'formik'
import '@fontsource/dm-sans/900.css' // Defaults to weight 400

import {
  DARK_GREY,
  LIGHT_GREY,
  METAL,
  MIDDLE_GREY,
  RED,
  SILVER,
  TEXT_MEDIUM,
  TEXT_SMALL,
  WHITE,
} from '../../../constants'
import { formikShape } from '../../../types'
import hasFieldFormikError from '../../../lib/hasFieldFormikError'

import Text from '../Text'
import FormikError from '../FormikError'

const Container = styled.div`
  position: relative;
  min-height: 35px;
  max-height: 70px;
  border-radius: 5px;
  background-color: ${WHITE};
  border: 1px solid ${({ isError }) => (isError ? RED : LIGHT_GREY)};
  ${({ isUpload }) => isUpload && 'opacity: 0'};
  ${({ isUpload }) => isUpload && 'height: 55px'};
  ${({ isUpload }) => !isUpload && 'padding: 10px 10px 12px 10px'};
  ${({ setWidth }) => setWidth && 'width: ' + setWidth};

  &:focus-within {
    outline-color: ${SILVER};
    outline-offset: 0px;
    outline-style: auto;
    outline-width: 2px;
  }
`

const Tooltip = styled.div`
  position: relative;
  ${({ isUpload }) => isUpload && 'height: 100%'};

  :before {
    content: '';
    display: none;
    border-left: 5px solid transparent;
    border-right: 5px solid transparent;
    border-bottom: 5px solid ${DARK_GREY};
    position: absolute;
    top: 30px;
    left: 7px;
  }
  :after {
    z-index: 1000;
    display: none;
    content: attr(data-tip);
    position: absolute;
    top: 35px;
    padding: 5px 8px;
    background: ${DARK_GREY};
    color: ${WHITE};
    font-size: ${TEXT_SMALL};
    border-radius: 3px;
    word-wrap: break-word;
    max-width: 300px;
  }
  :hover:before,
  :hover:after {
    ${({ content }) => content && content.length > 35 && 'display:block'};
  }
`

const StyledInput = styled.input`
  width: 100%;
  display: inline-block;
  font-family: 'DM Sans';
  color: ${METAL};
  font-size: ${TEXT_MEDIUM};
  margin: 0;
  background-color: ${WHITE};
  border: 1px solid ${LIGHT_GREY};
  transition: all 0.3s;
  border: none;
  outline: none;
  cursor: pointer;
`

const CharacterCount = styled.span`
  font-size: 11px;
  position: absolute;
  right: 5px;
  top: 1px;
  font-style: italic;
`

const Input = connect(
  ({
    formik: { handleChange, values, errors, touched },
    label,
    maximumLength = 100,
    name,
    isUpload,
    setWidth,
    type,
    ...props
  }) => {
    const [characterCount, setCharacterCount] = useState(null)

    return (
      <Container
        isError={hasFieldFormikError(errors, touched, name)}
        isUpload={isUpload}
        setWidth={setWidth}
      >
        <label>
          {label && (
            <Text color={SILVER} size={TEXT_SMALL}>
              {label}
            </Text>
          )}
          <Tooltip
            data-tip={getIn(values, name)}
            content={getIn(values, name)}
            isUpload={isUpload}
          >
            <StyledInput
              onChange={getIn(handleChange)}
              value={getIn(values, name)}
              name={name}
              type={type}
              maxLength={maximumLength}
              onKeyUp={({ target: { value } }) =>
                setCharacterCount(value.length)
              }
              {...props}
            />
          </Tooltip>
          {characterCount > 0 && type !== 'number' && (
            <CharacterCount>
              {characterCount} / {maximumLength}
            </CharacterCount>
          )}
        </label>
        <FormikError name={name} />
      </Container>
    )
  }
)

Input.propTypes = {
  formik: formikShape,
  label: string,
  name: string.isRequired,
}
Input.displayName = 'Input'

export default Input
